
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Response} from '../core/Response';
import {FieldModelResponse} from '../core/Response';
import {DrawingStorageChartData} from './DrawingStorageChartData';
import {FieldModelDrawingStorageChartData} from './DrawingStorageChartData';

/**
 * DrawingStorageChartsResponse struct
 */
class DrawingStorageChartsResponse extends Response {
  /**
   * Initialize struct
   * @param {!Response=} parent
   * @param {!string=} source
   * @param {!Array=} charts
   * @constructor
   */
  constructor (parent = new Response(), source = '', charts = []) {
    super()
    super.copy(parent)
    this.source = source
    this.charts = charts
  }

  /**
   * Copy struct (shallow copy)
   * @this {!DrawingStorageChartsResponse}
   * @param {!DrawingStorageChartsResponse} other Other struct
   * @returns {!DrawingStorageChartsResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.source != null) {
      this.source = other.source
    } else {
      this.source = null
    }
    if (other.charts != null) {
      this.charts = []
      for (let item of other.charts) {
        if (item != null) {
          let tempItem
          tempItem = DrawingStorageChartData.fromObject(item)
          this.charts.push(tempItem)
        } else {
          this.charts.push(null)
        }
      }
    } else {
      this.charts = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!DrawingStorageChartsResponse}
   * @returns {!DrawingStorageChartsResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new DrawingStorageChartsResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new DrawingStorageChartsResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!DrawingStorageChartsResponse}
   * @param {!DrawingStorageChartsResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof DrawingStorageChartsResponse)) {
      throw new TypeError('Instance of DrawingStorageChartsResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!DrawingStorageChartsResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      source: ((this.source != null) ? this.source : null),
      charts: ((this.charts != null) ? Array.from(this.charts, item => ((item != null) ? item : null)) : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return DrawingStorageChartsResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!DrawingStorageChartsResponse} other Object value
   * @returns {!DrawingStorageChartsResponse} Created struct
   */
  static fromObject (other) {
    return new DrawingStorageChartsResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!DrawingStorageChartsResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return DrawingStorageChartsResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!DrawingStorageChartsResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 10022
  }
}

export { DrawingStorageChartsResponse };

/**
 * Fast Binary Encoding DrawingStorageChartsResponse field model
 */
class FieldModelDrawingStorageChartsResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelResponse(buffer, 4 + 4)
    this.source = new fbe.FieldModelString(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.charts = new fbe.FieldModelVector(new FieldModelDrawingStorageChartData(buffer, this.source.fbeOffset + this.source.fbeSize), buffer, this.source.fbeOffset + this.source.fbeSize)
  }

  /**
   * Get the core.Response field model
   * @this {!FieldModelDrawingStorageChartsResponse}
   * @returns {!FieldModelResponse} core.Response field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelDrawingStorageChartsResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelDrawingStorageChartsResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.source.fbeSize + this.charts.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelDrawingStorageChartsResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.source.fbeExtra + this.charts.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelDrawingStorageChartsResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelDrawingStorageChartsResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelDrawingStorageChartsResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 10022
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelDrawingStorageChartsResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelDrawingStorageChartsResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.source.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.source.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.source.fbeSize

    if ((fbeCurrentSize + this.charts.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.charts.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.charts.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelDrawingStorageChartsResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelDrawingStorageChartsResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelDrawingStorageChartsResponse}
   * @param {!DrawingStorageChartsResponse} fbeValue Default value, defaults is new DrawingStorageChartsResponse()
   * @returns {!DrawingStorageChartsResponse} DrawingStorageChartsResponse value
   */
  get (fbeValue = new DrawingStorageChartsResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelDrawingStorageChartsResponse}
   * @param {!DrawingStorageChartsResponse} fbeValue DrawingStorageChartsResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.source.fbeSize) <= fbeStructSize) {
      fbeValue.source = this.source.get()
    } else {
      fbeValue.source = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.source.fbeSize

    if ((fbeCurrentSize + this.charts.fbeSize) <= fbeStructSize) {
      this.charts.get(fbeValue.charts)
    } else {
      fbeValue.charts.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.charts.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelDrawingStorageChartsResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelDrawingStorageChartsResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelDrawingStorageChartsResponse}
   * @param {!DrawingStorageChartsResponse} fbeValue DrawingStorageChartsResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelDrawingStorageChartsResponse}
   * @param {DrawingStorageChartsResponse} fbeValue DrawingStorageChartsResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.source.set(fbeValue.source)
    this.charts.set(fbeValue.charts)
  }
}

export { FieldModelDrawingStorageChartsResponse };

/**
 * Fast Binary Encoding DrawingStorageChartsResponse model
 */
class DrawingStorageChartsResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelDrawingStorageChartsResponse(this.buffer, 4)
  }

  /**
   * Get the DrawingStorageChartsResponse model
   * @this {!DrawingStorageChartsResponseModel}
   * @returns {!FieldModelDrawingStorageChartsResponse} model DrawingStorageChartsResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!DrawingStorageChartsResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!DrawingStorageChartsResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return DrawingStorageChartsResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!DrawingStorageChartsResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelDrawingStorageChartsResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!DrawingStorageChartsResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!DrawingStorageChartsResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!DrawingStorageChartsResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!DrawingStorageChartsResponseModel}
   * @param {!DrawingStorageChartsResponse} value DrawingStorageChartsResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!DrawingStorageChartsResponseModel}
   * @param {!DrawingStorageChartsResponse} value DrawingStorageChartsResponse value, defaults is new DrawingStorageChartsResponse()
   * @return {!object} Deserialized DrawingStorageChartsResponse value and its size
   */
  deserialize (value = new DrawingStorageChartsResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new DrawingStorageChartsResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new DrawingStorageChartsResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!DrawingStorageChartsResponseModel}
   * @param {!number} prev Previous DrawingStorageChartsResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { DrawingStorageChartsResponseModel };
